/**
 * Authentication Service
 * 
 * Handles all Supabase Auth operations including login, logout,
 * session management, and password reset.
 */

import { supabase, handleSupabaseError } from '../config/supabaseClient';

/**
 * Sign in with email and password
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise<{user: Object|null, session: Object|null, error: Object|null}>}
 */
export const signIn = async (email, password) => {
    try {
        const { data, error } = await supabase.auth.signInWithPassword({
            email,
            password,
        });

        if (error) {
            return { user: null, session: null, error: handleSupabaseError(error) };
        }

        return {
            user: data.user,
            session: data.session,
            error: null,
        };
    } catch (err) {
        return { user: null, session: null, error: handleSupabaseError(err) };
    }
};

/**
 * Sign out the current user
 * @returns {Promise<{success: boolean, error: Object|null}>}
 */
export const signOut = async () => {
    try {
        const { error } = await supabase.auth.signOut();

        if (error) {
            return { success: false, error: handleSupabaseError(error) };
        }

        return { success: true, error: null };
    } catch (err) {
        return { success: false, error: handleSupabaseError(err) };
    }
};

/**
 * Get the current user session
 * @returns {Promise<{user: Object|null, session: Object|null, error: Object|null}>}
 */
export const getCurrentUser = async () => {
    try {
        const { data, error } = await supabase.auth.getSession();

        if (error) {
            return { user: null, session: null, error: handleSupabaseError(error) };
        }

        return {
            user: data.session?.user || null,
            session: data.session,
            error: null,
        };
    } catch (err) {
        return { user: null, session: null, error: handleSupabaseError(err) };
    }
};

/**
 * Listen to auth state changes
 * @param {Function} callback - Function to call when auth state changes
 * @returns {Object} - Subscription object with unsubscribe method
 */
export const onAuthStateChange = (callback) => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
        callback(event, session);
    });

    return subscription;
};

/**
 * Send password reset email
 * @param {string} email - User email
 * @returns {Promise<{success: boolean, error: Object|null}>}
 */
export const sendPasswordResetEmail = async (email) => {
    try {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
            redirectTo: `${window.location.origin}/admin`,
        });

        if (error) {
            return { success: false, error: handleSupabaseError(error) };
        }

        return { success: true, error: null };
    } catch (err) {
        return { success: false, error: handleSupabaseError(err) };
    }
};

/**
 * Update user password
 * @param {string} newPassword - New password
 * @returns {Promise<{success: boolean, error: Object|null}>}
 */
export const updatePassword = async (newPassword) => {
    try {
        const { error } = await supabase.auth.updateUser({
            password: newPassword,
        });

        if (error) {
            return { success: false, error: handleSupabaseError(error) };
        }

        return { success: true, error: null };
    } catch (err) {
        return { success: false, error: handleSupabaseError(err) };
    }
};

/**
 * Check if user is authenticated
 * @returns {Promise<boolean>}
 */
export const isAuthenticated = async () => {
    try {
        const { data } = await supabase.auth.getSession();
        return !!data.session;
    } catch {
        return false;
    }
};

/**
 * Get access token for API calls
 * @returns {Promise<{token: string|null, error: Object|null}>}
 */
export const getAccessToken = async () => {
    try {
        const { data, error } = await supabase.auth.getSession();

        if (error) {
            return { token: null, error: handleSupabaseError(error) };
        }

        return {
            token: data.session?.access_token || null,
            error: null,
        };
    } catch (err) {
        return { token: null, error: handleSupabaseError(err) };
    }
};

/**
 * Refresh the current session
 * @returns {Promise<{session: Object|null, error: Object|null}>}
 */
export const refreshSession = async () => {
    try {
        const { data, error } = await supabase.auth.refreshSession();

        if (error) {
            return { session: null, error: handleSupabaseError(error) };
        }

        return { session: data.session, error: null };
    } catch (err) {
        return { session: null, error: handleSupabaseError(err) };
    }
};
